import debounce from "lodash/debounce"
import { useEffect, useState } from "react"

import theme from "@theme"

export const getPxUnit = (pxValue: string) =>
  parseInt(pxValue.replace("px", ""))

export const isMobile = () => {
  if (typeof document !== "undefined") {
    return document.body.clientWidth < getPxUnit(theme.screens["sm"])
  }

  return false
}

export const isTablet = () => {
  if (typeof document !== "undefined") {
    return (
      document.body.clientWidth >= getPxUnit(theme.screens["sm"]) &&
      document.body.clientWidth < getPxUnit(theme.screens["md"])
    )
  }

  return false
}

export const isLaptop = () => {
  if (typeof document !== "undefined") {
    return (
      document.body.clientWidth >= getPxUnit(theme.screens["md"]) &&
      document.body.clientWidth < getPxUnit(theme.screens["lg"])
    )
  }

  return false
}

export const isDesktop = () => {
  if (typeof document !== "undefined") {
    return document.body.clientWidth >= getPxUnit(theme.screens["lg"])
  }

  return false
}

const breakpointUtils = {
  mobile: isMobile,
  tablet: isTablet,
  laptop: isLaptop,
  desktop: isDesktop,
}

/**
 * Reactively check whether breakpoint has been reached.
 *
 * Use by passing in one of our V2 screen utils.
 *
 * @example From a component:
 * ```tsx
 * const isMobile = useIsBreakpoint("mobile")
 * ```
 */
export const useIsBreakpoint = (
  utilKey: keyof typeof breakpointUtils
): boolean => {
  const [isBreakpoint, setIsBreakpoint] = useState(breakpointUtils[utilKey])

  useEffect(() => {
    const windowResized = debounce(
      () => {
        setIsBreakpoint(breakpointUtils[utilKey])
      },
      500,
      { trailing: true }
    )

    window.addEventListener("resize", windowResized, { passive: true })

    setIsBreakpoint(breakpointUtils[utilKey])

    return () => {
      window.removeEventListener("resize", windowResized)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return isBreakpoint
}
